import { useEffect, useMemo, useRef, useState } from 'react'

/** One row in the picker: `value` is what the caller gets back, `label` is shown and matched, `sub` is the second line (e.g. Tamil name). */
export interface PickerOption {
  value: string
  label: string
  sub?: string | null
  right?: string | null
  rightSub?: string | null
  rightClass?: string
  /** Shown greyed out and cannot be picked (e.g. a catalogue product with no stock to sell). */
  disabled?: boolean
}

interface Props {
  options: PickerOption[]
  text: string
  onText: (text: string) => void
  onSelect: (opt: PickerOption) => void
  placeholder?: string
  className?: string
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> & Record<`data-${string}`, string>
  maxRows?: number
  emptyText?: string
  /** When given, the list ends with a "+ Add new item" row (Zoho-style) that calls this with the text typed so far. */
  onAddNew?: (typed: string) => void
  addNewLabel?: string
}

const MAX_DEFAULT = 40

/** Zoho-style type-ahead: filters on every word typed (name or second line), arrow keys move, Enter/Tab pick, Esc closes. */
export function ProductPicker({ options, text, onText, onSelect, placeholder, className, inputProps, maxRows = MAX_DEFAULT, emptyText = 'No matching item', onAddNew, addNewLabel = '+ Add new item' }: Props) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const matches = useMemo(() => {
    const q = text.trim().toLowerCase()
    const exact = options.find((o) => o.label.toLowerCase() === q)
    if (!q || exact) return options.slice(0, maxRows)
    const terms = q.split(/\s+/)
    const scored = options
      .map((o) => { const hay = `${o.label} ${o.sub || ''}`.toLowerCase(); const ok = terms.every((t) => hay.includes(t)); return ok ? { o, score: o.label.toLowerCase().startsWith(q) ? 0 : hay.indexOf(terms[0]) } : null })
      .filter(Boolean) as Array<{ o: PickerOption; score: number }>
    return scored.sort((a, b) => a.score - b.score).map((s) => s.o).slice(0, maxRows)
  }, [options, text, maxRows])

  useEffect(() => { setActive(0) }, [text, open])
  useEffect(() => {
    if (!open) return
    const el = listRef.current?.children[active] as HTMLElement | undefined
    el?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  const pick = (opt: PickerOption) => { if (opt.disabled) return; onSelect(opt); setOpen(false) }
  const isExact = options.some((o) => o.label.toLowerCase() === text.trim().toLowerCase())
  const addIndex = onAddNew ? matches.length : -1 // the add row sits after the matches and is reachable with the arrow keys
  const rowCount = matches.length + (onAddNew ? 1 : 0)
  const addNew = () => { if (!onAddNew) return; setOpen(false); onAddNew(text) }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, rowCount - 1)); return }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); return }
    if (e.key === 'Escape') { setOpen(false); return }
    if (e.key === 'Enter') { if (open && active === addIndex) { e.preventDefault(); addNew(); return } if (open && matches[active]) { e.preventDefault(); pick(matches[active]) } return }
    if (e.key === 'Tab' && !e.shiftKey && open && text.trim() && !isExact && matches[active]) { pick(matches[active]) }
    inputProps?.onKeyDown?.(e)
  }

  return (
    <div ref={wrapRef} className="relative" style={{ minWidth: 0 }}>
      <input
        type="text"
        autoComplete="off"
        {...inputProps}
        value={text}
        placeholder={placeholder}
        className={className}
        onChange={(e) => { onText(e.target.value); setOpen(true) }}
        onFocus={(e) => { setOpen(true); inputProps?.onFocus?.(e) }}
        onClick={(e) => { setOpen(true); inputProps?.onClick?.(e) }}
        onBlur={(e) => { setOpen(false); inputProps?.onBlur?.(e) }}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {open && (
        <div
          ref={listRef}
          className="picker-list"
          onMouseDown={(e) => e.preventDefault()}
        >
          {matches.length === 0 && <div className="picker-empty">{emptyText}</div>}
          {matches.map((o, i) => (
            <div key={o.value} className={`picker-row ${i === active ? 'on' : ''} ${o.disabled ? 'off' : ''}`} onMouseEnter={() => setActive(i)} onClick={() => pick(o)} aria-disabled={o.disabled || undefined}>
              <div className="picker-main">
                <div className="picker-label">{o.label}</div>
                {o.sub && <div className="picker-sub">{o.sub}</div>}
              </div>
              {(o.right || o.rightSub) && (
                <div className="picker-right">
                  {o.right && <div className={o.rightClass || ''}>{o.right}</div>}
                  {o.rightSub && <div className="picker-sub">{o.rightSub}</div>}
                </div>
              )}
            </div>
          ))}
          {options.length > matches.length && matches.length >= maxRows && <div className="picker-empty">Showing {maxRows} of {options.length} — keep typing to narrow down</div>}
          {onAddNew && (
            <div className={`picker-row picker-add ${active === addIndex ? 'on' : ''}`} onMouseEnter={() => setActive(addIndex)} onClick={addNew} data-testid="picker-add-new">
              <div className="picker-main">
                <div className="picker-label">{addNewLabel}</div>
                <div className="picker-sub">{text.trim() && !isExact ? `“${text.trim()}” is not in the list — add it to the catalogue` : 'Add a product that is not in the catalogue yet'}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
